import { getDateFromNric, getTermFromDob } from '@/utils/applicant';
import { getIsoDate } from '@/utils/date';
import { generateAprovalCode } from '@/utils/payment';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  useGetApplicationQuery,
  usePostDocumentMutation,
  usePostPaymentMutation,
} from '../apis';
import {
  APP_ID,
  MERCHANT_ID_INSURANCE,
  MERCHANT_ID_TAKAFUL,
} from '../constants';
import {
  Answer,
  ClientType,
  DocumentType,
  DistributionChannel,
  Entity,
  Language,
  Locale,
  PaymentMode,
  PaymentSource,
  PreferredLanguage,
  Product,
  ProductCode,
} from '../enums';
import { useAppDispatch, useAppSelector } from '../reducers';
import { updateFormValues, updateIsEdit } from '../reducers/application';
import { useReviewSchema } from '../schemas/review';
import { PaymentSummary } from '../types/component';
import { ReviewFormValues } from '../types/form';
import { useApplication } from './useApplication';
import { useRoute } from './useRoute';
import { ApplicationApplicant } from '../types/api';
import { Agent } from '../types/model';
import { pushDataLayer } from '@/utils/googleAnalytics';
import { useGAFactory } from './useGAFactory';
import { getPreferredLang } from '@/utils/preferredLang';
import { NS_REVIEW } from '@/i18n/namespaces';
import { useTranslation } from 'react-i18next';

export const useReview = () => {
  const dispatch = useAppDispatch();
  const { queries } = useRoute();
  const refId = queries.get('refId');
  const application = useAppSelector((state) => state.application);
  const { amount, paymentMode, getApplicationRequestData } = useApplication();
  // const [postApplication, { isLoading }] = usePostApplicationMutation();
  const [postPayment, { isLoading }] = usePostPaymentMutation();
  const [postDocument] = usePostDocumentMutation();
  const { data, isLoading: isLoadingGetApplication } = useGetApplicationQuery({
    refId: application.refId ?? refId ?? '',
  });
  const { entity, redirect, product } = useRoute();
  const { i18n } = useTranslation(NS_REVIEW);
  const isEasyHealth = product === Product.EasyHealth;
  const { reviewSchema } = useReviewSchema();
  const GAFactory = useGAFactory();

  const triggerDocumentGeneration = () => {
    const email =
      application.formValues.application?.policy_owner.personalDetail.email ??
      '';
    const language = i18n.language === Locale.EN ? Language.ENG : Language.BMY;
    const requestBase = {
      appId: application.appId,
      refId: application.refId ?? '',
      email,
      Language: language,
      preferredLang: getPreferredLang(i18n.language),
    };

    [DocumentType.AF, DocumentType.SI, DocumentType.PDS].forEach(
      (documentType) => {
        void postDocument({ ...requestBase, documentType })
          .unwrap()
          .catch((error) => {
            console.error(
              `Failed to pre-trigger ${documentType} generation:`,
              error,
            );
          });
      },
    );
  };

  const fetchApp = async () => {
    if (refId) {
      try {
        const cleanedApplicants = data?.data.applicants.map(
          ({ isBeneficialOwner, ...rest }) => rest,
        );
        const result = await postPayment({
          body: {
            appId: APP_ID,
            refId,
            productCode: data?.data.productCode as ProductCode,
            paymentMode: data?.data.paymentMode as PaymentMode,
            applicants: cleanedApplicants as ApplicationApplicant[],
            agent: data?.data.agent as Agent,
            distributionChannel: data?.data
              .distributionChannel as DistributionChannel,
            payMethod: PaymentSource.CreditCard,
            preferredLang: data?.data.preferredLang as PreferredLanguage,
            purposeOfTrx: data?.data.purposeOfTrx,
            consentConfirmation: false,
            paymentConsent: false,
            collection: {
              approvalDate: getIsoDate(),
              approvalCode: generateAprovalCode(),
              merchantId:
                entity === Entity.INSURANCE
                  ? MERCHANT_ID_INSURANCE
                  : MERCHANT_ID_TAKAFUL,
              trxAmount: data?.data.collection?.trxAmount || amount,
              trxId:
                data?.data.applicants.find((applicant) =>
                  [
                    ClientType.ProposerOrLifeAssured,
                    ClientType.LifeAssured,
                  ].includes(applicant.clientType),
                )?.proposalNumber ?? '',
              source: PaymentSource.CreditCard,
            },
            payout: data?.data.payout,
            productType: product,
            entityType: entity,
          },
          params: true,
        });
        if (result.data?.detail.paymentUrl)
          window.open(result.data?.detail.paymentUrl, '_self');
      } catch (error) {
        console.error('Failed to fetch application:', error);
      }
    }
  };

  useEffect(() => {
    fetchApp();
  }, [refId]);

  useEffect(() => {
    if (!application.formValues?.quotation) {
      redirect('quotation');
    }
  }, [application.formValues.quotation]);

  const formMethods = useForm<ReviewFormValues>({
    resolver: yupResolver(reviewSchema),
    defaultValues: application.formValues.review,
    mode: 'onChange',
  });

  const onSubmit = async (formValues: ReviewFormValues) => {
    dispatch(updateFormValues({ review: formValues }));
    triggerDocumentGeneration();
    pushDataLayer(
      GAFactory('add_payment_info', {
        planCode: application.planCode,
        paymentModeFormValue: application.paymentMode,
        deductible: application.deductible,
      }),
    );
    try {
      const getApplicationData = getApplicationRequestData(
        application.formValues?.application!,
      );
      const updatedConsent = getApplicationData.applicants.map((applicant) => ({
        ...applicant,
        pdpaConsent: formValues.marketingConsent ? Answer.Yes : Answer.No,
      }));
      const result = await postPayment({
        body: {
          ...getApplicationData,
          promoCode: formValues.promoCode,
          applicants: updatedConsent,
          paymentConsent: Boolean(formValues?.declaration?.debitCredit),
          consentConfirmation: false,
          collection: {
            approvalDate: getIsoDate(),
            approvalCode: generateAprovalCode(),
            merchantId:
              entity === Entity.INSURANCE
                ? MERCHANT_ID_INSURANCE
                : MERCHANT_ID_TAKAFUL,
            trxAmount: amount,
            trxId:
              data?.data.applicants.find((applicant) =>
                [
                  ClientType.ProposerOrLifeAssured,
                  ClientType.LifeAssured,
                ].includes(applicant.clientType),
              )?.proposalNumber ?? '',
            source: PaymentSource.CreditCard,
          },
          payout: {
            accountHolderName:
              application.formValues.application?.bankDetail.fullname ?? '',
            accountNo:
              application.formValues.application?.bankDetail.accountNo ?? '',
            bankCode:
              application.formValues.application?.bankDetail.bankName ?? '',
            source: PaymentSource.Direct,
          },
          productType: product,
          entityType: entity,
        },
        params: true,
      });
      if (result.data?.detail.paymentUrl)
        window.open(result.data?.detail.paymentUrl, '_self');
    } catch (error) {
      redirect('error');
    }
  };

  let dob = new Date();
  if (application.formValues.quotation) {
    dob = getDateFromNric(
      application.formValues.quotation?.life_insured?.nric ??
        application.formValues.quotation?.policy_owner?.nric ??
        '',
    );
  }
  const summary: PaymentSummary = {
    startDate: new Date(),
    endDate:
      product === Product.EzyMedical
        ? getTermFromDob(dob, 84, 30)
        : getTermFromDob(dob),
    name:
      (application.formValues.quotation?.life_insured !== null
        ? application.formValues.quotation?.life_insured.name
        : application.formValues.quotation?.policy_owner?.name) ?? '',
    nric:
      (application.formValues.quotation?.life_insured !== null
        ? application.formValues.quotation?.life_insured.nric
        : application.formValues.quotation?.policy_owner?.nric) ?? '',
    planCode: application.planCode ?? '',
    deductible: application.deductible,
    totalAmount: amount,
  };

  const onEdit = () => {
    dispatch(updateIsEdit(true));
    redirect('plan');
  };

  const onBack = () => {
    redirect('application');
  };

  return {
    summary,
    paymentMode,
    formMethods,
    isLoading: isLoading || isLoadingGetApplication,
    isEasyHealth,
    onEdit,
    onSubmit,
    onBack,
  };
};
